import { Modal, useFormatting } from "@msys/ui";
import {
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  type ChangeLogActionGroup,
  describeFieldValue,
} from "./changeLogGrouping.js";
import {
  REFERENCE_VALUE_FIELDS,
  useChangeLogLabels,
} from "./changeLogLabels.js";
import type {
  ChangeLogEntity,
  ChangeLogEntityStrategy,
  ChangeLogEntry,
  ChangeLogFieldValue,
} from "./changeLogTypes.js";

function formatValue(value: unknown): string {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return JSON.stringify(value);
}

/** The fixed glyph for a value cell whose content is deliberately withheld (design D2) — a
 * literal character, never a translation key, so it renders identically in every locale. */
const EM_DASH = "—";

/**
 * The shared em-dash affordance (design D2) for a value cell whose content is withheld: a
 * `recorded: false` side (design D2), and a registry-covered reference miss — a recorded,
 * non-empty value on a `REFERENCE_VALUE_FIELDS`-covered field with no resolved `label`
 * (design D6, task 4.3). Parameterized purely by the already-localized tooltip TEXT — never
 * the raw key — so each caller resolves its own `ChangeLog` key (the two states genuinely
 * differ: "not recorded" vs "no longer resolvable") while sharing one render.
 */
function renderWithheldValue(tooltipText: string): React.ReactNode {
  return (
    <Tooltip title={tooltipText}>
      <Typography component="span" variant="body2" color="textSecondary">
        {EM_DASH}
      </Typography>
    </Tooltip>
  );
}

interface Props {
  group: ChangeLogActionGroup;
  entityStrategy: ChangeLogEntityStrategy;
  handleClose: () => void;
}

/**
 * Detail modal for one action (design D4/D9), read entirely from the already-loaded
 * `correlationId` group (no refetch). Entries are partitioned by `(operation, field
 * present)` into four buckets, because a field edit, a lifecycle snapshot, and an entity
 * lifecycle marker are different shapes of data (design D5):
 *  - `CHANGED` entries that carry a value → a four-column field table (item · localised
 *    field · tri-state old value · new value), item linked via the `ChangeLogEntityStrategy`.
 *    Each value cell renders one of three fixed tokens (design D2): `—` + an explanatory
 *    tooltip for a not-recorded (`recorded: false`) side, "(empty)" for a recorded-empty
 *    side, else the resolved value — never a silently blank cell.
 *  - per-field `CREATED` snapshots (field present) → a new-value table (item · field · new value).
 *  - per-field `DELETED` snapshots (field present) → an old-value table (item · field · old
 *    value), the old value being the pre-deletion state.
 *  - field-less lifecycle markers, pure events (null operation) and value-less `CHANGED`
 *    entries → a plain list, one line per entity, composed in code as a "«entity» verb"
 *    sentence (the linked entity + the localised lowercase `operation` verb; D6/D8).
 * A mixed action shows the tables first, then the list. The modal title is the group's
 * derived "Action · Target" (design D3/D6) — the same composition as the list row — not a
 * provenance/trigger label; the body still renders every entry, including the suppressed
 * `pricing.*` cascade changes.
 */
export function ChangeLogDetailModal({
  group,
  entityStrategy,
  handleClose,
}: Props) {
  const { t } = useTranslate(["ChangeLog", "Global"]);
  const { getFormattedDateTime } = useFormatting();
  const labels = useChangeLogLabels();

  const actorLabel = labels.actorLabel(group.actor);

  // Title: the derived "Action · Target" (design D3/D6), the same composition as the list
  // row — not a stored summary/trigger. The body below is unaffected and still renders
  // every entry, including the `pricing.*` cascade entries suppressed from the list row.
  const modalTitle = [
    labels.actionLabel(group),
    labels.targetDisplay(group, entityStrategy).label,
  ]
    .filter(Boolean)
    .join(" · ");

  const renderEntityCell = (entity: ChangeLogEntity): React.ReactNode => {
    // The viewer-local composite name (RF-279 D6) supersedes the server-composed
    // `entity.name` here too, so a work session reads the same in the body as in the title
    // instead of showing raw ISO instants. Null for every other kind, so their cells keep
    // the read-time-resolved live name — deliberately NOT the name as of the action, which
    // is the list Target's job.
    const label = entityStrategy.entityLabel(
      entity,
      labels.compositeEntityName(group, entity)
    );
    const to = entityStrategy.entityLink(entity);
    if (to) {
      return (
        <Link component={RouterLink} to={to} underline="hover">
          {label}
        </Link>
      );
    }
    return (
      <Typography component="span" variant="body2">
        {label}
      </Typography>
    );
  };

  const renderValue = (
    field: string | null | undefined,
    fieldValue: ChangeLogFieldValue | undefined
  ): React.ReactNode => {
    const display = describeFieldValue(fieldValue);
    switch (display.kind) {
      // design D2: a not-recorded side (masked, marker, oversize-guarded, or a side the
      // action never recorded) renders the shared em-dash affordance — the wire cannot (and
      // must not) distinguish the causes, so one honest tooltip covers them all. Replaces
      // the former inline "content hidden" text, whose key is deleted from the platform.
      case "hidden":
        return renderWithheldValue(
          t("This value is not shown — it was hidden or too large to record.", {
            ns: "ChangeLog",
          })
        );
      case "empty":
        return (
          <Typography component="span" variant="body2" color="textSecondary">
            {t("(empty)", { ns: "Global" })}
          </Typography>
        );
      case "value": {
        // Resolution order (design D5/D6): a read-model-resolved reference label (phase /
        // lead / role / assignee / building name / …) supersedes the raw id/value for this
        // cell; failing that, a registry-covered reference field (`REFERENCE_VALUE_FIELDS`,
        // the frontend mirror of `resolveChangeLogFieldLabel`) with no label renders the
        // shared em-dash affordance — the read model TRIED to resolve this id and missed, so
        // the raw uuid must never leak; failing that (an uncovered field), the labels hook's
        // `fieldValueText` (design D5: date classes / Yes-No / curated enums — the work
        // session's raw ISO span ends, RF-279 D6, sit beside them); failing all three, the
        // existing raw-value formatting stands. Read per side, so each cell resolves
        // independently. The `hidden` / `empty` states above are untouched.
        const label = fieldValue?.label;
        if (label != null) {
          return (
            <Typography component="span" variant="body2">
              {label}
            </Typography>
          );
        }
        if (field != null && REFERENCE_VALUE_FIELDS.has(field)) {
          return renderWithheldValue(
            t(
              "This reference can no longer be resolved — it may have been deleted.",
              { ns: "ChangeLog" }
            )
          );
        }
        const localised = labels.fieldValueText(field, display.value);
        return (
          <Typography component="span" variant="body2">
            {localised ?? formatValue(display.value)}
          </Typography>
        );
      }
    }
  };

  const renderChangedRow = (entry: ChangeLogEntry): React.ReactNode => (
    <TableRow key={entry.id}>
      <TableCell>{renderEntityCell(entry.entity)}</TableCell>
      <TableCell>{labels.fieldLabel(entry)}</TableCell>
      <TableCell>{renderValue(entry.field, entry.oldValue)}</TableCell>
      <TableCell>{renderValue(entry.field, entry.newValue)}</TableCell>
    </TableRow>
  );

  // A one-sided lifecycle snapshot row (design D5): item · localised field · the single
  // recorded value (`newValue` for a CREATED snapshot, `oldValue` — the pre-deletion state —
  // for a DELETED snapshot), reusing the same tri-state renderer as the CHANGED table.
  const renderSnapshotRow = (
    entry: ChangeLogEntry,
    side: "oldValue" | "newValue"
  ): React.ReactNode => (
    <TableRow key={entry.id}>
      <TableCell>{renderEntityCell(entry.entity)}</TableCell>
      <TableCell>{labels.fieldLabel(entry)}</TableCell>
      <TableCell>{renderValue(entry.field, entry[side])}</TableCell>
    </TableRow>
  );

  // A per-operation snapshot table (design D5): the localised CRUD verb (`Created`/`Deleted`
  // from the change-log namespace, reusing the existing lifecycle-line label — no new key) as
  // a section label, over a three-column table whose value header/column depend on the side.
  // The verb vocabulary is kind-independent (design D1), so the section label needs no kind
  // of its own — every entry in the table speaks the same verb.
  const renderSnapshotTable = (
    entries: ChangeLogEntry[],
    operation: "CREATED" | "DELETED",
    side: "oldValue" | "newValue",
    valueHeader: string
  ): React.ReactNode => (
    <Stack direction="column" spacing={0.5}>
      <Typography variant="subtitle2">
        {labels.operationLabel(operation)}
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t("Item", { ns: "Global" })}</TableCell>
            <TableCell>{t("Field", { ns: "ChangeLog" })}</TableCell>
            <TableCell>{valueHeader}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>{entries.map(e => renderSnapshotRow(e, side))}</TableBody>
      </Table>
    </Stack>
  );

  // Lifecycle / value-less lines render as the composed "«entity» verb" sentence (design
  // D6/D8): the «resolved entity» (linked when live) then the localised lowercase CRUD verb
  // (a `ChangeLog` key) — glued in code, never a single translated string. A null operation
  // (pure event) has no verb and renders the entity alone. The verb vocabulary is
  // kind-independent (design D1), so it needs no entity kind of its own.
  const renderLifecycleLine = (entry: ChangeLogEntry): React.ReactNode => {
    const entity = renderEntityCell(entry.entity);
    const verb = labels.operationVerb(entry.operation);
    return (
      <Typography key={entry.id} component="div" variant="body2">
        «{entity}»{verb != null ? <> {verb}</> : null}
      </Typography>
    );
  };

  // Entries are partitioned by `(operation, field present)` (design D5/D9). Every
  // field-bearing `CHANGED` entry earns a table row (design D4) — including a masked/
  // marker field whose both sides are `recorded: false`, which has no before/after to
  // diff but still HAS a field, so it renders its own row (localised label, `—` + tooltip
  // in both cells) instead of being diverted to the lifecycle-line fallback, where the
  // per-entity dedup used to drop it silently whenever the entity also had a visible
  // change. A field-less `CHANGED` (no field code, so `isFieldEntry` never captured
  // values for it) cannot render a row and keeps the generic lifecycle-line path.
  const isRenderableChange = (e: ChangeLogEntry): boolean =>
    e.operation === "CHANGED" && e.field != null;

  // Per-field lifecycle snapshots (field present) earn a one-sided snapshot table only
  // when the recorded side is a REAL value (design D3): capture already skips `null` on a
  // snapshot, but not `""`/whitespace-only/`[]` (design D1's widened empty branch), so this
  // closes that hole at display time — covering historical rows that already stored `""`
  // as well as future ones. A field-less CREATED/DELETED marker (no `field`) stays a
  // generic line, unaffected by this value filter.
  const isCreatedSnapshot = (e: ChangeLogEntry): boolean =>
    e.operation === "CREATED" &&
    e.field != null &&
    describeFieldValue(e.newValue).kind === "value";
  const isDeletedSnapshot = (e: ChangeLogEntry): boolean =>
    e.operation === "DELETED" &&
    e.field != null &&
    describeFieldValue(e.oldValue).kind === "value";

  const changedEntries = group.entries.filter(isRenderableChange);
  const createdEntries = group.entries.filter(isCreatedSnapshot);
  const deletedEntries = group.entries.filter(isDeletedSnapshot);

  // One generic line per (operation, entity), for everything not in a table: field-less
  // lifecycle markers and pure events. Every field-bearing `CHANGED` is now in the
  // CHANGED table (design D4), so a value-less `CHANGED` can no longer reach this filter at
  // all; a field-less lifecycle marker is always kept, so a create/delete still reads as a
  // "Created/Deleted: entity" line even alongside its snapshot table — and a snapshot table
  // filtered down to nothing (design D3) still leaves that marker line as the only trace of
  // the creation/deletion, which is the point.
  const seenLifecycle = new Set<string>();
  const lifecycleEntries = group.entries.filter(e => {
    if (isRenderableChange(e)) return false;
    if (isCreatedSnapshot(e) || isDeletedSnapshot(e)) return false;
    const key = `${e.operation ?? "null"}:${e.entity.entityId}`;
    if (seenLifecycle.has(key)) return false;
    seenLifecycle.add(key);
    return true;
  });

  return (
    <Modal
      title={modalTitle}
      dialogProps={{ maxWidth: "md" }}
      handleClose={handleClose}
      actionButtons={[
        {
          label: t("Close", { ns: "Global" }),
          handleClick: handleClose,
          buttonProps: { variant: "text" },
        },
      ]}
    >
      <Stack direction="column" spacing={1.5}>
        <Typography variant="caption" color="textSecondary">
          {actorLabel} · {getFormattedDateTime(group.occurredAt)}
        </Typography>
        {changedEntries.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("Item", { ns: "Global" })}</TableCell>
                <TableCell>{t("Field", { ns: "ChangeLog" })}</TableCell>
                <TableCell>{t("Old value", { ns: "ChangeLog" })}</TableCell>
                <TableCell>{t("New value", { ns: "ChangeLog" })}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>{changedEntries.map(renderChangedRow)}</TableBody>
          </Table>
        )}
        {createdEntries.length > 0 &&
          renderSnapshotTable(
            createdEntries,
            "CREATED",
            "newValue",
            t("New value", { ns: "ChangeLog" })
          )}
        {deletedEntries.length > 0 &&
          renderSnapshotTable(
            deletedEntries,
            "DELETED",
            "oldValue",
            t("Old value", { ns: "ChangeLog" })
          )}
        {lifecycleEntries.length > 0 && (
          <Stack direction="column" spacing={0.5}>
            {lifecycleEntries.map(renderLifecycleLine)}
          </Stack>
        )}
      </Stack>
    </Modal>
  );
}
